/**
 * reports-fill — occupancy / fill-rate model for the ADMIN Reports Overview.
 *
 * The admin Overview is a fill-rate console (spec: Event-Performance-Report-Build-Spec.md):
 * it groups event OCCURRENCES by month → event name → occurrence and ranks them
 * by fill rate (booked / spaces). The contract has no multi-occurrence "event
 * chain + fill" section yet, so this maps the workspace's REAL events
 * (useEvents → listEvents) 1:1 into occurrences — every number on the page is
 * derived from op-backed event data (capacity, registrations, location, date);
 * nothing is fabricated. Every number is derived here once so the stats bar,
 * KPI cards, leaderboards and drill-down all reconcile.
 *
 *   Fill mapping (per the "decide for me" answer):
 *     spaces  ← event.capacity            booked ← event.registered
 *     store   ← event.location            fill   = round(booked / spaces * 100)
 *
 * CLAUDE-CODE dependency (REQ-026 in CLAUDE-CODE-REQUESTS.md): a real
 * occupancy report returning occurrences (id, eventId, datetime, location,
 * spaces, booked) replaces buildFillData at wiring time — adding true
 * multi-occurrence chains — with no UI change.
 *
 * WIRED (2026-07-10): `fetchFillOccurrences()` below calls the real
 * `getOccupancyReport` op when it exists (typeof-guarded) and maps its rows
 * 1:1 into FillOccurrence — true recurring-event occurrence chains, real
 * occurrence ids, real per-occurrence start times. `buildFillData` remains the
 * fallback (op missing, or the call throws) so this file degrades exactly to
 * its prior behavior rather than breaking. No UI change either way.
 *
 * Pure TypeScript (no JSX) so it can be imported anywhere.
 */
import * as apiClient from "@v0y4ge/api-client";
import { REPORTS_OVERVIEW_OPTS } from "../../shell/request-opts";
import type { UIEvent } from "../../lib/events-adapter";

/* ── One event occurrence ("event chain" row) ─────────────────────────── */
export interface FillOccurrence {
  id: string;
  eventId: string;
  name: string;         // event name (grouping key L2)
  datetime: number;     // epoch ms
  spaces: number;       // total capacity
  booked: number;       // attendees booked
  storeId: string;
  storeName: string;    // location, e.g. "Acme #42 — Downtown"
  instanceId: string;
  displayDate: string;  // "15 Jan 2026"
  time: string;         // "6:00 PM"
  monthYear: string;    // "January 2026"  (grouping key L1)
  monthSort: string;    // "202601"        (sort key)
  fill: number;         // 0..100, guarded against spaces===0
  when: "past" | "future";
}

/* ── Fill-rate colour coding (spec §4), mapped to jrni semantic tokens ─── */
export type FillTone = "strong" | "moderate" | "attention";
export function fillTone(pct: number): FillTone {
  if (pct >= 85) return "strong";
  if (pct >= 50) return "moderate";
  return "attention";
}
/** Human label so colour is never the ONLY signal (accessibility). */
export const FILL_TONE_LABEL: Record<FillTone, string> = {
  strong: "Strong", moderate: "Moderate", attention: "Needs attention",
};

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
function to12h(d: Date): string {
  let h = d.getHours(); const m = d.getMinutes(); const ap = h >= 12 ? "PM" : "AM";
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, "0")} ${ap}`;
}
function occFrom(eventId: string, name: string, dt: Date, spaces: number, booked: number, store: { id: string; name: string }, idx: number, now: number): FillOccurrence {
  const ms = dt.getTime();
  const sp = Math.max(0, Math.round(spaces));
  const bk = Math.max(0, Math.min(sp || Infinity, Math.round(booked)));
  return {
    id: `${eventId}-o${idx}`,
    eventId, name, datetime: ms,
    spaces: sp, booked: bk,
    storeId: store.id, storeName: store.name,
    instanceId: eventId, // the REAL event id — no synthesized instance numbers
    displayDate: `${dt.getDate()} ${MONTHS[dt.getMonth()].slice(0, 3)} ${dt.getFullYear()}`,
    time: to12h(dt),
    monthYear: `${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`,
    monthSort: `${dt.getFullYear()}${String(dt.getMonth() + 1).padStart(2, "0")}`,
    fill: sp > 0 ? Math.round((bk / sp) * 100) : 0,
    when: ms < now ? "past" : "future",
  };
}

/** Map the workspace's real events (useEvents → UIEvent) 1:1 into fill
 * occurrences, so the whole report reconciles with the app's Events data.
 * Each event contributes ONE occurrence at its own location with its real
 * capacity + registrations; drafts and cancelled events are excluded. Both
 * events that have already run (status past/live) and upcoming ones are kept,
 * so the drill-down shows real run + future events and every roster download
 * maps to a real event. FALLBACK path — used only when getOccupancyReport is
 * absent or errors; see fetchFillOccurrences below for the preferred path. */
export function buildFillData(events: UIEvent[], now = Date.now()): FillOccurrence[] {
  return events
    .filter((e) => e.status !== "draft" && e.status !== "canceled")
    .map((e) => {
      const startIso = (e.raw && (e.raw as any).startsAt) || "";
      const dt = startIso ? new Date(startIso) : new Date(now);
      const spaces = e.capacity && e.capacity > 0 ? e.capacity : 0;
      return occFrom(e.id, e.title, dt, spaces, e.registered || 0, { id: `loc_${e.id}`, name: e.location || "\u2014" }, 0, now);
    });
}

/** Map one server OccupancyOccurrenceDto row (REQ-026) into a FillOccurrence.
 * `id` is the server's stable per-occurrence id (`${eventId}-o${index}`,
 * unchanged across different [from,to] windows); `instanceId` stays the real
 * eventId (no synthesized instance numbers, same rule as the fallback path). */
function fromOccupancyRow(r: Record<string, unknown>, now: number): FillOccurrence {
  const parsed = new Date(String(r.startsAt ?? ""));
  const ms = isNaN(parsed.getTime()) ? now : parsed.getTime();
  const dt = new Date(ms);
  const spaces = Math.max(0, Math.round(Number(r.spaces) || 0));
  const booked = Math.max(0, Math.min(spaces || Infinity, Math.round(Number(r.booked) || 0)));
  return {
    id: String(r.id ?? `${String(r.eventId ?? "")}-o0`),
    eventId: String(r.eventId ?? ""),
    name: String(r.name ?? "Untitled event"),
    datetime: ms, spaces, booked,
    storeId: String(r.locationId ?? ""),
    storeName: String(r.locationName ?? "\u2014"),
    instanceId: String(r.eventId ?? ""),
    displayDate: `${dt.getDate()} ${MONTHS[dt.getMonth()].slice(0, 3)} ${dt.getFullYear()}`,
    time: to12h(dt),
    monthYear: `${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`,
    monthSort: `${dt.getFullYear()}${String(dt.getMonth() + 1).padStart(2, "0")}`,
    fill: spaces > 0 ? Math.round((booked / spaces) * 100) : 0,
    when: ms < now ? "past" : "future",
  };
}

/** Preferred data source for the admin Reports Overview (REQ-026, wired
 * 2026-07-10). Calls the real `getOccupancyReport` with a wide static window
 * (the screen's own Analyze/Compare controls do the actual date slicing
 * client-side against whatever this returns, same as the fallback's "all
 * events" semantics) so recurring events expand into their TRUE occurrence
 * chain instead of the 1:1 client-side event map. Falls back to
 * `buildFillData` when the op is absent (typeof-guarded) or the call throws —
 * same shape either way, no UI change. */
export async function fetchFillOccurrences(events: UIEvent[], now = Date.now()): Promise<FillOccurrence[]> {
  const fn = (apiClient as Record<string, unknown>).getOccupancyReport;
  if (typeof fn === "function") {
    try {
      const res = await (fn as (q: Record<string, unknown>, o?: unknown) => Promise<{ occurrences?: Record<string, unknown>[] }>)(
        { from: "2015-01-01", to: "2045-12-31" },
        REPORTS_OVERVIEW_OPTS,
      );
      const rows = Array.isArray(res?.occurrences) ? res!.occurrences! : [];
      return rows.map((r) => fromOccupancyRow(r, now));
    } catch (e) {
      console.warn("getOccupancyReport failed, falling back to the client-side event map:", e);
    }
  }
  return buildFillData(events, now);
}

/* ── Aggregations (single source of truth for every number on the page) ── */
export interface FillStats { stores: number; events: number; attendees: number; spaces: number; fill: number; }
export function globalStats(all: FillOccurrence[]): FillStats {
  const spaces = all.reduce((s, o) => s + o.spaces, 0);
  const attendees = all.reduce((s, o) => s + o.booked, 0);
  return {
    stores: new Set(all.map((o) => o.storeName)).size,
    events: all.length,
    attendees, spaces,
    fill: spaces > 0 ? Math.round((attendees / spaces) * 100) : 0,
  };
}
export interface ExecMetrics { efficiency: number; perfect: number; missed: number; attendees: number; }
export function execMetrics(rows: FillOccurrence[]): ExecMetrics {
  const spaces = rows.reduce((s, o) => s + o.spaces, 0);
  const booked = rows.reduce((s, o) => s + o.booked, 0);
  return {
    efficiency: spaces > 0 ? Math.round((booked / spaces) * 100) : 0,
    perfect: rows.filter((o) => o.fill >= 80).length,
    missed: rows.filter((o) => o.fill < 30).length,
    attendees: booked,
  };
}

/* Grouped drill-down: month (asc by monthSort) → event name → occurrences. */
export interface EventGroup { name: string; occurrences: FillOccurrence[]; booked: number; spaces: number; fill: number; }
export interface MonthGroup { monthYear: string; monthSort: string; events: EventGroup[] }
export function groupForDrilldown(rows: FillOccurrence[]): MonthGroup[] {
  const byMonth = new Map<string, FillOccurrence[]>();
  rows.forEach((o) => { const a = byMonth.get(o.monthSort) || []; a.push(o); byMonth.set(o.monthSort, a); });
  return [...byMonth.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([monthSort, occs]) => {
      const byEvent = new Map<string, FillOccurrence[]>();
      occs.forEach((o) => { const a = byEvent.get(o.name) || []; a.push(o); byEvent.set(o.name, a); });
      const events: EventGroup[] = [...byEvent.entries()].map(([name, list]) => {
        const spaces = list.reduce((s, o) => s + o.spaces, 0);
        const booked = list.reduce((s, o) => s + o.booked, 0);
        return { name, occurrences: list.sort((a, b) => a.datetime - b.datetime), booked, spaces, fill: spaces > 0 ? Math.round((booked / spaces) * 100) : 0 };
      }).sort((a, b) => b.fill - a.fill);
      return { monthYear: occs[0].monthYear, monthSort, events };
    });
}

/* Distinct stores for the location filter, derived from the occurrence set so
 * the filter and the drill-down always agree. */
export function storeOptions(all: FillOccurrence[]): { id: string; name: string }[] {
  const seen = new Map<string, string>();
  all.forEach((o) => { if (!seen.has(o.storeName)) seen.set(o.storeName, o.storeId); });
  return [...seen.entries()].map(([name, id]) => ({ id: name, name })).sort((a, b) => a.name.localeCompare(b.name));
}
